import React from "react"
import { ArrowRight, Package, Globe, Truck, Shield, Box, Terminal } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useScrollAnimation } from "../hooks/useScrollAnimation"

export default function Hero() {
    const { t } = useTranslation()
    const ref = useScrollAnimation()

    return (
        <section id="hero" ref={ref} className="scroll-animate relative pt-20 pb-12 lg:pt-24 lg:pb-32 overflow-hidden bg-slate-950">
            {/* Background Gradient SVG Effect */}
            <div className="absolute inset-0 z-0 opacity-30">
                <svg className="absolute inset-0 w-full h-full animate-pulse" preserveAspectRatio="none" viewBox="0 0 100 100">
                    <defs>
                        <linearGradient id="heroGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style={{ stopColor: '#193cb8', stopOpacity: 1 }} />
                            <stop offset="50%" style={{ stopColor: 'rgba(25, 60, 184, 0.3)', stopOpacity: 1 }} />
                            <stop offset="100%" style={{ stopColor: '#0CB81F', stopOpacity: 1 }} />
                        </linearGradient>
                    </defs>
                    <polygon points="0,0 100,0 100,100 0,0" fill="url(#heroGradient)" />
                </svg>
            </div>

            <div className="container mx-auto px-4 md:px-6 relative z-10">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    <div className="text-center lg:text-left">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-500 text-xs font-medium mb-6 ring-1 ring-inset ring-emerald-500/20">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                            </span>
                            {t('hero.badge')}
                        </div>

                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6 leading-[1.1]">
                            {t('hero.headline')}
                        </h1>

                        <div className="space-y-4 mb-10">
                            <p className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-lg mx-auto lg:mx-0">
                                <span className="text-white font-medium">{t('hero.description_part1')}</span> {t('hero.description_part2')}
                            </p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                            <button
                                className="group relative px-6 py-3 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-xl transition-all duration-300 transform hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(16,185,129,0.3)] flex items-center justify-center overflow-hidden"
                                onClick={() => document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' })}
                            >
                                <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                                <span className="relative flex items-center text-sm md:text-base">
                                    {t('hero.waitlist')}
                                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </span>
                            </button>
                            <button
                                className="px-6 py-3 bg-transparent hover:bg-white/5 text-white font-medium rounded-xl border border-white/10 transition-all duration-300 flex items-center justify-center group text-sm md:text-base"
                                onClick={() => window.location.href = 'mailto:info@bousanmtechnology.com'}
                            >
                                {t('hero.contact')}
                                <ArrowRight className="ml-2 w-4 h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                            </button>
                        </div>

                        <div className="mt-8 flex flex-wrap justify-center lg:justify-start gap-6 opacity-60">
                            {[
                                { icon: Shield, text: t('hero.ukRegistered') },
                                { icon: Terminal, text: "Bespoke Code" },
                                { icon: Truck, text: "Global Logistics" }
                            ].map((item, i) => (
                                <div key={i} className="flex items-center space-x-2 text-xs text-slate-300">
                                    <item.icon className="w-3.5 h-3.5 text-emerald-500" />
                                    <span>{item.text}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="relative mx-auto w-full max-w-[480px]" style={{ perspective: '1200px' }}>
                        {/* Logistics Dashboard/Card Visual */}
                        <div className="relative group transition-all duration-700 ease-out transform"
                            style={{ transformStyle: 'preserve-3d' }}>
                            <div className="relative aspect-[1.6/1] rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5)] p-8 text-white border border-white/10 overflow-hidden transform group-hover:rotate-y-12 group-hover:rotate-x-2 group-hover:scale-105 transition-all duration-500">
                                {/* Decorative Blur */}
                                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl -mr-32 -mt-32"></div>
                                <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl -ml-32 -mb-32"></div>

                                <div className="relative z-10 h-full flex flex-col justify-between">
                                    <div className="flex justify-between items-start">
                                        <div className="space-y-1">
                                            <div className="text-emerald-500 font-bold tracking-tighter text-xl">BOUSANM</div>
                                            <div className="text-[10px] text-emerald-500/50 font-medium tracking-[0.2em] uppercase">Tech & Logistics</div>
                                        </div>
                                        <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-emerald-500 transition-colors">
                                            <Package className="w-6 h-6 text-emerald-600" />
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div className="flex items-center space-x-3">
                                            <div className="w-10 h-7 bg-white/5 rounded-md border border-white/5 flex items-center justify-center">
                                                <Box className="w-4 h-4 text-white/40" />
                                            </div>
                                            <div className="h-0.5 flex-1 bg-gradient-to-r from-emerald-500/40 via-emerald-500/80 to-transparent rounded-full shadow-[0_0_15px_rgba(16,185,129,0.4)]"></div>
                                        </div>
                                        <div className="text-2xl font-mono text-white tracking-[0.15em] drop-shadow-lg">
                                            {t('hero.secure')}
                                        </div>
                                    </div>

                                    <div className="flex justify-between items-end">
                                        <div>
                                            <div className="text-[10px] text-white/40 mb-1 uppercase tracking-widest">{t('hero.cardLabel')}</div>
                                            <div className="text-lg font-medium tracking-wide text-emerald-500 drop-shadow-lg">{t('hero.businessName')}</div>
                                        </div>
                                        <div className="flex -space-x-3">
                                            <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center backdrop-blur-md shadow-lg">
                                                <Globe className="w-5 h-5 text-emerald-500" />
                                            </div>
                                            <div className="w-10 h-10 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center backdrop-blur-md shadow-lg">
                                                <Truck className="w-5 h-5 text-blue-400" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Floating UI Widget */}
                            <div className="absolute -bottom-10 -right-6 lg:-right-12 bg-slate-900/90 backdrop-blur-xl border border-white/10 p-4 rounded-2xl shadow-2xl animate-bounce-slow hidden sm:block">
                                <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                                        <Truck className="w-5 h-5 text-emerald-500" />
                                    </div>
                                    <div>
                                        <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Status</div>
                                        <div className="text-sm font-bold text-white">In Transit</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
